import { Button, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import enLocale from 'date-fns/locale/en-US';
import frLocale from 'date-fns/locale/fr-CA';
import useTimezone from 'hooks/useTimezone';
import { useModal } from 'mui-modal-provider';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getTypeFromName } from 'utils/stringUtils';
import BasicTable from 'views/components/BasicTable';
import LinkViewButton from 'views/components/buttons/LinkViewButton';
import SubCard from 'views/components/cards/SubCard';
import DeleteDocumentButton from './DocumentDeleteButton';
import UploadDocumentDialog from './UploadDocumentDialog';
import useDocuments, { Document } from './useDocuments';

interface Props {
  userId: string;
}

export default function UploadedDocuments({ userId }: Props) {
  const { documents, refetchDocuments } = useDocuments(userId);
  const { showModal } = useModal();
  const intl = useIntl();
  const { timezone } = useTimezone();

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: intl.formatMessage({ defaultMessage: 'File Name' }),
      minWidth: 150,
      renderCell: (params) => {
        const document = params.row as Document;
        return (
          <>
            <strong style={{ marginRight: 8 }}>{document.metadata.name}</strong>({document.name})
          </>
        );
      }
    },
    {
      field: 'uploadedBy',
      headerName: intl.formatMessage({ defaultMessage: 'Uploaded By' }),
      minWidth: 150,
      renderCell: (params) => {
        const document = params.row as Document;
        return document.metadata.uploadedBy.fullName;
      }
    },
    {
      field: 'fileType',
      headerName: intl.formatMessage({ defaultMessage: 'File Type' }),
      minWidth: 150,
      renderCell: (params) => {
        const document = params.row as Document;
        return getTypeFromName(document.name);
      }
    },
    {
      field: 'uploadedDate',
      minWidth: 160,
      headerName: intl.formatMessage({ defaultMessage: 'Uploaded Date' }),
      renderCell: (params) => {
        const document = params.row as Document;

        const updatedAt = document.updatedAt ?? null;

        return updatedAt
          ? format(utcToZonedTime(updatedAt, timezone), intl.locale === 'fr' ? 'do MMMM yyyy, HH:mm' : 'MMMM do yyyy, h:mm a', {
              locale: intl.locale === 'fr' ? frLocale : enLocale
            })
          : '-';
      }
    },
    {
      field: '',
      width: 220,
      renderCell: (params) => {
        const document = params.row as Document;
        return (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <LinkViewButton getLinkUrl={`/user/${userId}/documents/${document._id}/get-link`} />
            <DeleteDocumentButton userId={userId} documentId={document._id} onDeleteSuccess={refetchDocuments} />
          </Stack>
        );
      }
    }
  ];

  const handleUpload = useCallback(() => {
    const modal = showModal(UploadDocumentDialog, {
      userId,
      onSubmitSuccess: refetchDocuments,
      onClose: () => modal.hide()
    });
  }, [userId, showModal, refetchDocuments]);

  return (
    <SubCard
      title={<FormattedMessage defaultMessage="Uploaded Documents" />}
      secondary={
        <Button variant="contained" color="primary" onClick={handleUpload}>
          <FormattedMessage defaultMessage="Upload" />
        </Button>
      }
    >
      <BasicTable
        emptyMessage={<FormattedMessage defaultMessage="No documents found." />}
        columns={columns}
        rows={documents}
        paginationMode="client"
      />
    </SubCard>
  );
}
